import { useMutation } from '@tanstack/react-query';
import { useNotify, useRefresh, useTranslate } from 'ra-core';
import { updateOfferNegotiation } from '../../../dataProvider/dataProvider';
import { Offer } from '../types';
import BaseNegotiationModal from './BaseNegotiationModal';
import { declineReasons } from '../choices';
import SecondaryButton from '../../../components/ui/SecondaryButton';
import { RefreshCw, Ban } from 'lucide-react';
import ErrorButton from '../../../components/ui/ErrorButton';
import DefaultAlert from '../../../components/ui/DefaultAlert';
import { useWatch, useFormContext } from 'react-hook-form';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

type Props = {
  offer: Offer;
  open: boolean;
  onClose: () => void;
  onCounter: () => void;
};

function DeclineOfferModal({ offer, open, onClose, onCounter }: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Record<string, any>) =>
      updateOfferNegotiation('publisher-declined', data),
    onSuccess: () => {
      void refresh();
      notify(
        <DefaultAlert
          severity="success"
          alertTitle={t('offer.declineModal.notification.success')}
        />
      );
      onClose();
    },
    onError: error =>
      notify(
        <DefaultAlert
          severity="error"
          alertTitle={t('offer.declineModal.notification.error')}
          alertDescription={error?.message}
        />
      ),
  });

  const handleSubmit = (formData: any) => {
    mutate({
      parent_id: offer.last_negotiation_id,
      action_reason: formData.negotiation.decline_reasons,
      action_note: formData.negotiation.action_note,
    });
  };

  return (
    <BaseNegotiationModal
      open={open}
      onClose={onClose}
      title={t('offer.declineModal.title')}
      onSubmit={handleSubmit}
      actions={
        <DeclineFormActions isPending={isPending} onCounter={onCounter} />
      }
    >
      <p className="mb-4 text-foreground">
        {t('offer.declineModal.description')}
      </p>
      <DeclineReasonsSelector label={t('offer.declineModal.label.reason')} />
      <ActionNoteField
        label={t('offer.common.label.message')}
        placeholder={t('offer.common.placeholder.message')}
      />
    </BaseNegotiationModal>
  );
}

export default DeclineOfferModal;

function DeclineReasonsSelector({ label }: { label: string }) {
  const { setValue, watch } = useFormContext();
  const selected: string[] = watch('negotiation.decline_reasons') || [];

  const toggle = (id: string) => {
    setValue(
      'negotiation.decline_reasons',
      selected.includes(id) ? selected.filter(r => r !== id) : [...selected, id]
    );
  };

  return (
    <div className="flex flex-col gap-2 mb-4">
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      <div className="grid grid-cols-2 gap-2">
        {declineReasons.map(reason => (
          <label
            key={reason.id}
            className="flex items-center gap-2 cursor-pointer select-none rounded-lg border border-border bg-card p-3 hover:bg-accent transition-colors"
          >
            <Checkbox
              checked={selected.includes(reason.id)}
              onCheckedChange={() => toggle(reason.id)}
            />
            <span className="text-sm text-foreground">{reason.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function ActionNoteField({
  label,
  placeholder,
}: {
  label: string;
  placeholder: string;
}) {
  const { register } = useFormContext();
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      <Textarea
        {...register('negotiation.action_note')}
        rows={3}
        placeholder={placeholder}
        variant="muted"
      />
    </div>
  );
}

const DeclineFormActions = ({
  isPending,
  onCounter,
}: {
  isPending: boolean;
  onCounter: () => void;
}) => {
  const t = useTranslate();
  const decline_reasons = useWatch({ name: 'negotiation.decline_reasons' });
  const hasDeclineReason = decline_reasons?.length > 0;

  return (
    <>
      <SecondaryButton onClick={onCounter} size="lg">
        <RefreshCw className="size-4" />
        {t('offer.declineModal.button.counter')}
      </SecondaryButton>
      <ErrorButton
        type="submit"
        disabled={!hasDeclineReason || isPending}
        size="lg"
      >
        <Ban className="size-4" />
        {t('offer.declineModal.button.decline')}
      </ErrorButton>
    </>
  );
};
